/**
 * TransactionFieldTable Component
 *
 * Desktop: table with columns (Order, Field name, Type, For, In use, Actions).
 * Mobile: sticky Add button + stack of sortable cards (same style as CurrencySettings mobile).
 * Parent must wrap with DndContext and provide DragOverlay if desired.
 */

import React from "react";
import {
  Button,
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import type { TransactionCustomField, TransactionCustomFieldScopeType } from "../../../types";
import SortableTransactionFieldRow, { TRANSACTION_FIELD_TABLE_CELL_SX } from "./SortableTransactionFieldRow";

function formatScopeList(types: string[]): string {
  if (!types || types.length === 0) return "—";
  return types.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(", ");
}

export interface TransactionFieldTableProps {
  visibleFields: TransactionCustomField[];
  inUseSet: Set<string>;
  usageLoading?: boolean;
  saving: boolean;
  onEdit: (field: TransactionCustomField) => void;
  onHide: (field: TransactionCustomField) => void;
  onDeleteClick: (field: TransactionCustomField) => void;
  isMobile: boolean;
  /** When set, table is for a single type (section header shows type); "For" column can show this. */
  transactionType?: TransactionCustomFieldScopeType;
  /** When true, Add is in section header; table shows no Add button and empty state says "Use + above". */
  addButtonInHeader?: boolean;
  /** When true, all edit actions (edit, hide, delete) are disabled. */
  readOnly?: boolean;
}

export default function TransactionFieldTable({
  visibleFields,
  inUseSet,
  usageLoading,
  saving,
  onEdit,
  onHide,
  onDeleteClick,
  isMobile,
  transactionType: sectionType,
  addButtonInHeader,
  readOnly = false,
}: TransactionFieldTableProps): React.ReactElement {
  const forListForField = (f: TransactionCustomField) =>
    sectionType != null
      ? sectionType.charAt(0).toUpperCase() + sectionType.slice(1)
      : formatScopeList(f.transactionTypes ?? (f.transactionType ? [f.transactionType] : []));

  const emptyMessage = addButtonInHeader
    ? "No fields yet. Use the + button above to add."
    : "No custom fields yet. Add one to get started.";

  if (isMobile) {
    return (
      <>
        {visibleFields.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: "center",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Paper>
        ) : (
          <>
            <Stack spacing={0.5}>
              {visibleFields.map((field) => (
                <SortableTransactionFieldRow
                    key={field.id}
                    field={field}
                    forList={forListForField(field)}
                    inUse={inUseSet.has(field.id)}
                    usageLoading={usageLoading}
                    saving={saving}
                    onEdit={onEdit}
                    onHide={onHide}
                    onDeleteClick={onDeleteClick}
                    isMobile
                    readOnly={readOnly}
                  />
                ))}
            </Stack>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        variant="outlined"
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflowX: "auto",
          overflowY: "auto",
          bgcolor: "background.paper",
          width: "100%",
        }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%", minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.order, fontWeight: 600, color: "text.secondary" }}>Order</TableCell>
              <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.fieldName, fontWeight: 600, color: "text.secondary" }}>Field name</TableCell>
              <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.type, fontWeight: 600, color: "text.secondary" }}>Type</TableCell>
              <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.for, fontWeight: 600, color: "text.secondary" }}>For</TableCell>
              <TableCell align="right" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.actions, fontWeight: 600, color: "text.secondary" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleFields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {visibleFields.map((field) => (
                  <SortableTransactionFieldRow
                    key={field.id}
                    field={field}
                    forList={forListForField(field)}
                    inUse={inUseSet.has(field.id)}
                    usageLoading={usageLoading}
                    saving={saving}
                    onEdit={onEdit}
                    onHide={onHide}
                    onDeleteClick={onDeleteClick}
                    readOnly={readOnly}
                  />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
